import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { Link } from '@/components/design-system';
import {
  FONT_WEIGHTS,
  GAP_SCALE,
  PADDING_X,
  PADDING_Y,
  SIZE_SCALE,
  SPACE_Y_SCALE,
  TYPE_SCALE,
  COLOR_SCALE,
  RADIUS_SCALE,
  INTERACTIVE_STATES,
  FLEX,
  FLEX_ALIGN as FLEX_ALIGN,
  FLEX_JUSTIFY as FLEX_JUSTIFY,
  SPACE_X_SCALE,
  MARGIN_TOP,
  PADDING_BOTTOM,
  PADDING_TOP,
  POSITION_SCALE,
  TEXT_TRANSFORM,
} from '@/components/design-system/tokens';
import { createPageUrl } from '@/utils';

import { useEscapeKey, useArrowNavigation } from '../hooks/useKeyboardNav';

// Navigation data generators
const createFindMyCareDropdownItems = (
  t: (ns: string, key: string, fallback: string) => string
) => [
  {
    path: 'Providers',
    label: t('navigation', 'providers', 'Providers'),
    description: t(
      'navigation',
      'find_my_care_providers_caption',
      'Explore accredited care partners'
    ),
  },
  {
    path: 'Doctors',
    label: t('navigation', 'doctors', 'Doctors'),
    description: t(
      'navigation',
      'find_my_care_doctors_caption',
      'Meet expert physicians across specialties'
    ),
  },
  {
    path: 'Treatments',
    label: t('navigation', 'treatments', 'Treatments'),
    description: t(
      'navigation',
      'find_my_care_treatments_caption',
      'Discover procedures tailored to you'
    ),
  },
  {
    path: 'Equipments',
    label: t('navigation', 'equipments', 'Equipments'),
    description: t(
      'navigation',
      'find_my_care_equipments_caption',
      'See the devices powering modern care'
    ),
  },
];

const createResourcesDropdownItems = (t: (ns: string, key: string, fallback: string) => string) => [
  {
    path: 'Blog',
    label: t('navigation', 'blog', 'Blog'),
    description: t(
      'navigation',
      'resources_blog_caption',
      'Read expert insights and patient stories'
    ),
  },
  {
    path: 'Glossary',
    label: t('navigation', 'glossary', 'Glossary'),
    description: t(
      'navigation',
      'resources_glossary_caption',
      'Understand medical terms with ease'
    ),
  },
  {
    path: 'AboutUs',
    label: t('navigation', 'about_us', 'About Us'),
    description: t(
      'navigation',
      'resources_about_us_caption',
      'Learn about Medipaths and our mission'
    ),
  },
  {
    path: 'HowItWorks',
    label: t('navigation', 'how_it_works', 'How It Works'),
    description: t(
      'navigation',
      'resources_how_it_works_caption',
      'See how Medipaths helps your care journey'
    ),
  },
];

interface DesktopNavProps {
  headerOnLight: boolean;
  showFindMyCareDropdown: boolean;
  setShowFindMyCareDropdown: (show: boolean) => void;
  t: (
    namespace: string,
    key: string,
    fallback?: string,
    variables?: Record<string, unknown>
  ) => string;
}

/**
 * Generate navigation link class based on active state and theme
 */
const generateNavLinkClass = (
  headerOnLight: boolean,
  path: string,
  normalizedPathname: string,
  isDropdownActive = false
) => {
  const targetUrl = createPageUrl(path);
  const isActive = normalizedPathname === targetUrl || isDropdownActive;
  return `group text-xs transition-all duration-300 ${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['1']} whitespace-nowrap ${PADDING_X['3']} ${PADDING_Y['1.5']} ${RADIUS_SCALE.full} ${POSITION_SCALE.relative} ${
    isActive
      ? headerOnLight
        ? `${COLOR_SCALE.foreground} font-bold ${COLOR_SCALE['surface-subtle']}`
        : `text-white font-bold ${COLOR_SCALE.surface}/10`
      : headerOnLight
        ? `${COLOR_SCALE['foreground-muted']} hover:${COLOR_SCALE.foreground} hover:${COLOR_SCALE['surface-muted']} font-medium`
        : `text-white/80 hover:text-white hover:${COLOR_SCALE.surface}/10 font-medium`
  }`;
};

/**
 * Get label for Find My Care dropdown based on current page
 */
const getFindMyCareLabel = (
  pathname: string,
  t: (ns: string, key: string, fallback: string) => string
) => {
  switch (pathname) {
    case createPageUrl('Providers'):
      return t('navigation', 'providers', 'Providers');
    case createPageUrl('Doctors'):
      return t('navigation', 'doctors', 'Doctors');
    case createPageUrl('Treatments'):
      return t('navigation', 'treatments', 'Treatments');
    case createPageUrl('Equipments'):
      return t('navigation', 'equipments', 'Equipments');
    default:
      return t('navigation', 'find_my_care', 'Find My Care');
  }
};

/**
 * Get label for Resources dropdown based on current page
 */
const getResourcesLabel = (
  pathname: string,
  t: (ns: string, key: string, fallback: string) => string
) => {
  switch (pathname) {
    case createPageUrl('Blog'):
      return t('navigation', 'blog', 'Blog');
    case createPageUrl('Glossary'):
      return t('navigation', 'glossary', 'Glossary');
    case createPageUrl('AboutUs'):
      return t('navigation', 'about_us', 'About Us');
    case createPageUrl('HowItWorks'):
      return t('navigation', 'how_it_works', 'How It Works');
    default:
      return t('navigation', 'resources', 'Resources');
  }
};

/**
 * Check if current path is in Find My Care section
 */
const isFindMyCarePath = (pathname: string) =>
  [
    createPageUrl('Providers'),
    createPageUrl('Doctors'),
    createPageUrl('Treatments'),
    createPageUrl('Equipments'),
  ].includes(pathname);

/**
 * Check if current path is in Resources section
 */
const isResourcesPath = (pathname: string) =>
  [
    createPageUrl('Blog'),
    createPageUrl('Glossary'),
    createPageUrl('AboutUs'),
    createPageUrl('HowItWorks'),
  ].includes(pathname);

export const DesktopNav = React.memo(
  ({ headerOnLight, showFindMyCareDropdown, setShowFindMyCareDropdown, t }: DesktopNavProps) => {
    const location = useLocation();
    const dropdownRef = useRef<HTMLDivElement>(null);
    const resourcesDropdownRef = useRef<HTMLDivElement>(null);
    const [showResourcesDropdown, setShowResourcesDropdown] = useState(false);

    const dropdownItems = useMemo(() => createFindMyCareDropdownItems(t), [t]);
    const resourcesItems = useMemo(() => createResourcesDropdownItems(t), [t]);

    // Close dropdown handler
    const closeDropdown = useCallback(() => {
      setShowFindMyCareDropdown(false);
      setShowResourcesDropdown(false);
    }, [setShowFindMyCareDropdown]);

    // Keyboard navigation
    useEscapeKey(showFindMyCareDropdown, closeDropdown);
    useArrowNavigation(showFindMyCareDropdown, dropdownItems.length, dropdownRef);
    useEscapeKey(showResourcesDropdown, closeDropdown);
    useArrowNavigation(showResourcesDropdown, resourcesItems.length, resourcesDropdownRef);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          closeDropdown();
        }
        if (
          resourcesDropdownRef.current &&
          !resourcesDropdownRef.current.contains(event.target as Node)
        ) {
          setShowResourcesDropdown(false);
        }
      };

      if (showFindMyCareDropdown || showResourcesDropdown) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [showFindMyCareDropdown, showResourcesDropdown, closeDropdown]);

    // Normalize pathname by removing trailing slash for comparison
    const normalizedPathname =
      location.pathname.endsWith('/') && location.pathname.length > 1
        ? location.pathname.slice(0, -1)
        : location.pathname;

    const navLinkClass = (path: string, isDropdownActive = false) =>
      generateNavLinkClass(headerOnLight, path, normalizedPathname, isDropdownActive);

    // Check if we're on any of the Find My Care pages (only mark active if on child pages, not on dropdown open)
    const isFindMyCareActive = isFindMyCarePath(normalizedPathname);
    const isResourcesActive = isResourcesPath(normalizedPathname);

    // Get the current page label for dropdown button
    const findMyCareLabel = getFindMyCareLabel(location.pathname, t);
    const resourcesLabel = getResourcesLabel(location.pathname, t);

    return (
      <nav
        className={`hidden lg:${FLEX.flex} ${FLEX_ALIGN.center} ${SPACE_X_SCALE['4']} absolute left-1/2 -translate-x-1/2`}
      >
        <Link to={createPageUrl('Home')} className={navLinkClass('Home')}>
          <span
            className={`${TYPE_SCALE.xs} group-hover:-translate-y-0.5 transition-transform duration-200 inline-block`}
          >
            {t('navigation', 'home', 'Home')}
          </span>
        </Link>

        <div className={POSITION_SCALE.relative}>
          <button
            onClick={() => setShowFindMyCareDropdown(!showFindMyCareDropdown)}
            onFocus={() => setShowResourcesDropdown(false)}
            className={navLinkClass('', isFindMyCareActive)}
            aria-expanded={showFindMyCareDropdown}
            aria-controls="find-my-care-dropdown"
          >
            <span
              className={`${TYPE_SCALE.xs} group-hover:-translate-y-0.5 transition-transform duration-200 inline-block`}
            >
              {findMyCareLabel}
            </span>
            <ChevronDown
              className={`${SIZE_SCALE['3.5']} ${SIZE_SCALE['3.5']} transition-transform duration-200 ${
                showFindMyCareDropdown ? 'rotate-180' : ''
              }`}
            />
          </button>

          {showFindMyCareDropdown && (
            <div
              ref={dropdownRef}
              className={`absolute top-full left-1/2 -translate-x-1/2 ${MARGIN_TOP['3']} ${SIZE_SCALE.w72} max-w-sm ${RADIUS_SCALE['2xl']} ${COLOR_SCALE.surface}/95 ${PADDING_X['3']} ${PADDING_BOTTOM['3']} ${PADDING_TOP['2']} shadow-2xl shadow-blue-100/70 ring-1 ${INTERACTIVE_STATES.rings.hover} backdrop-blur-md z-[99999] `}
              id="find-my-care-dropdown"
              role="menu"
              aria-label={t('navigation', 'find_my_care_menu', 'Find My Care menu')}
            >
              <div className={`${PADDING_X['3']} ${PADDING_BOTTOM['3']} ${PADDING_TOP['2']}`}>
                <span
                  className={`${TYPE_SCALE['3xs']} ${FONT_WEIGHTS.semibold} ${TEXT_TRANSFORM.uppercase} tracking-[0.3em] ${COLOR_SCALE['blue-600']}`}
                >
                  {t('navigation', 'find_my_care', 'Find My Care')}
                </span>
                <p
                  className={`${MARGIN_TOP['1']} ${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']}`}
                >
                  {t(
                    'navigation',
                    'find_my_care_helper',
                    'Navigate your care journey with quick shortcuts'
                  )}
                </p>
              </div>
              <div className={`${SPACE_Y_SCALE['2']} ${PADDING_X['1']}`}>
                {dropdownItems.map(({ path, label, description }) => (
                  <Link
                    key={path}
                    to={createPageUrl(path)}
                    onClick={closeDropdown}
                    className={`group ${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.between} ${GAP_SCALE['3']} ${RADIUS_SCALE.xl} ${PADDING_X['4']} ${PADDING_Y['3']} ${INTERACTIVE_STATES.transitions.all} focus-visible:outline-none focus:outline-none hover:bg-blue-50/70`}
                    role="menuitem"
                  >
                    <div className={`${FLEX.flex} ${FLEX.flexCol}`}>
                      <span
                        className={`text-sm ${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground} ${INTERACTIVE_STATES.transitions.all} group-hover:text-blue-700`}
                      >
                        {label}
                      </span>
                      <span
                        className={`${MARGIN_TOP['0.5']} ${TYPE_SCALE.xs} ${COLOR_SCALE['foreground-muted']} ${INTERACTIVE_STATES.transitions.colors} group-hover:${COLOR_SCALE['slate-600']}`}
                      >
                        {description}
                      </span>
                    </div>
                    <ChevronRight
                      className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']} ${COLOR_SCALE['blue-600']} ${INTERACTIVE_STATES.transitions.all} group-hover:translate-x-1 focus:outline-none`}
                    />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={POSITION_SCALE.relative}>
          <button
            onClick={() => {
              setShowResourcesDropdown(prev => !prev);
              if (!showResourcesDropdown) {
                setShowFindMyCareDropdown(false);
              }
            }}
            className={navLinkClass('', isResourcesActive)}
            aria-expanded={showResourcesDropdown}
            aria-controls="resources-dropdown"
          >
            <span
              className={`${TYPE_SCALE.xs} group-hover:-translate-y-0.5 transition-transform duration-200 inline-block`}
            >
              {resourcesLabel}
            </span>
            <ChevronDown
              className={`${SIZE_SCALE['3.5']} ${SIZE_SCALE['3.5']} transition-transform duration-200 ${
                showResourcesDropdown ? 'rotate-180' : ''
              }`}
            />
          </button>

          {showResourcesDropdown && (
            <div
              ref={resourcesDropdownRef}
              className={`absolute top-full left-1/2 -translate-x-1/2 ${MARGIN_TOP['3']} ${SIZE_SCALE.w72} max-w-sm ${RADIUS_SCALE['2xl']} ${COLOR_SCALE.surface}/95 ${PADDING_X['3']} ${PADDING_BOTTOM['3']} ${PADDING_TOP['2']} shadow-2xl shadow-blue-100/70 ring-1 ${INTERACTIVE_STATES.rings.hover} backdrop-blur-md z-[99999] `}
              id="resources-dropdown"
              role="menu"
              aria-label={t('navigation', 'resources_menu', 'Resources menu')}
            >
              <div className={`${PADDING_X['3']} ${PADDING_BOTTOM['3']} ${PADDING_TOP['2']}`}>
                <span
                  className={`${TYPE_SCALE['3xs']} ${FONT_WEIGHTS.semibold} ${TEXT_TRANSFORM.uppercase} tracking-[0.3em] ${COLOR_SCALE['blue-600']}`}
                >
                  {t('navigation', 'resources', 'Resources')}
                </span>
                <p
                  className={`${MARGIN_TOP['1']} ${TYPE_SCALE.sm} ${COLOR_SCALE['foreground-muted']}`}
                >
                  {t('navigation', 'resources_helper', 'Stay informed with curated content')}
                </p>
              </div>
              <div className={`${SPACE_Y_SCALE['2']} ${PADDING_X['1']}`}>
                {resourcesItems.map(({ path, label, description }) => (
                  <Link
                    key={path}
                    to={createPageUrl(path)}
                    onClick={() => {
                      setShowResourcesDropdown(false);
                    }}
                    className={`group ${FLEX.flex} ${FLEX_ALIGN.center} ${FLEX_JUSTIFY.between} ${GAP_SCALE['3']} ${RADIUS_SCALE.xl} ${PADDING_X['4']} ${PADDING_Y['3']} ${INTERACTIVE_STATES.transitions.all} focus-visible:outline-none focus:outline-none hover:bg-blue-50/70`}
                    role="menuitem"
                  >
                    <div className={`${FLEX.flex} ${FLEX.flexCol}`}>
                      <span
                        className={`text-sm ${FONT_WEIGHTS.semibold} ${COLOR_SCALE.foreground} ${INTERACTIVE_STATES.transitions.all} group-hover:text-blue-700`}
                      >
                        {label}
                      </span>
                      <span
                        className={`${MARGIN_TOP['0.5']} ${TYPE_SCALE.xs} ${COLOR_SCALE['foreground-muted']} ${INTERACTIVE_STATES.transitions.colors} group-hover:${COLOR_SCALE['slate-600']}`}
                      >
                        {description}
                      </span>
                    </div>
                    <ChevronRight
                      className={`${SIZE_SCALE['4']} ${SIZE_SCALE['4']} ${COLOR_SCALE['blue-600']} ${INTERACTIVE_STATES.transitions.all} group-hover:translate-x-1 focus:outline-none`}
                    />
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>
    );
  }
);

DesktopNav.displayName = 'DesktopNav';
